import './App.css';
import { ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, matchPath } from 'react-router-dom';
import Layout from './components/layout/Layout';
import Dashboard from './components/pages/Dashboard';
import Library from './components/pages/Library';
import Installers from './components/pages/Installers';
import IlokPage from './components/pages/IlokPage';
import LicenseDetail from './components/pages/LicenseDetail';
import CreditsPage from './components/pages/CreditsPage';
import SubscriptionPage from './components/pages/SubscriptionPage';
import AccountPage from './components/pages/AccountPage';
import StorePage from './components/pages/StorePage';
import OffersPage from './components/pages/OffersPage';
import Products from './components/pages/Products';
import ProductPage from './components/pages/ProductPage';
import SubscriptionDetail from './components/pages/SubscriptionDetail';
import SkuPage from './components/pages/SkuPage';
import SkuEntitlements from './components/pages/SkuEntitlements';
import UserPage from './components/pages/UserPage';
import Users from './components/pages/Users';
import Skus from './components/pages/Skus';
import Organizations from './components/pages/Organizations';
import OrganizationPage from './components/pages/OrganizationPage';
import PointsPage from './components/pages/PointsPage';
import OrderDetail from './components/pages/OrderDetail';
import BuyPage from './components/pages/BuyPage';
import PurchaseSuccessPage from './components/pages/PurchaseSuccessPage';
import Redemptions from './components/pages/Redemptions';
import RedemptionDetail from './components/pages/RedemptionDetail';
import ResellerRedeemPage from './components/pages/ResellerRedeemPage';
import { RESELLER_PAGES } from './components/pages/resellerPages';
import RequirePrivileged from './components/RequirePrivileged';
import PolicyConsentDialog from './components/PolicyConsentDialog';
import GuestPurchasePage from './components/pages/GuestPurchasePage';
import MaintenancePage from './components/pages/MaintenancePage';
import { useMaintenance } from './api/maintenance';
import { Policy } from './api/models';
import Api, { ApiError } from './api/api';
import { useDispatch } from 'react-redux';
import { logIn } from './redux/globalSlice';
import { isExplicitSignOut, useSession } from './auth/session';
import { useSignOut } from './auth/signOut';
import { loginPageUrl } from './auth/amplify';
import { RENT_TO_OWN_ENABLED, REWARD_POINTS_ENABLED } from './featureFlags';
import { useOffersPageEnabled } from './offersAccess';

// Your Offers stays reachable for system-wide admins while withdrawn for
// everyone else (featureFlags.ts OFFERS_PAGE_ENABLED)
function OffersRoute() {
  return useOffersPageEnabled() ? <OffersPage /> : <Navigate to="/" replace />
}

export const cdnUrl = process.env.REACT_APP_CDN_URL;

// The /me bootstrap: fetched once per page load (not again when the access
// token rotates — the users slice keeps `me`), retried with backoff and on
// tab wake-up when it fails. It must never leave the app authenticated but
// without `me`: RequirePrivileged would spin forever and the sidebar would
// lose its privileged entries.
type Bootstrap = {
  status: "idle" | "loading" | "ready" | "error"
  attempt: number
  message?: string
  // 401 that survived apiFetch's own refresh-and-retry
  unauthorized?: boolean
}

const BOOTSTRAP_RETRY_DELAYS_MS = [5 * 1000, 15 * 1000, 60 * 1000]

function FullScreen({ children }: { children: ReactNode }) {
  return (
    <div style={{
      position: "relative", width: "100vw", height: "100vh",
      display: "flex", justifyContent: "center", alignItems: "center"
    }}>
      <div style={{ display: 'flex', flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 12, position: "relative", width: '100%', height: '100%' }}>
        {children}
      </div>
    </div>
  )
}

export default function App() {
  const { isLoading, isAuthenticated, accessToken, degraded, retry } = useSession();
  const maintenance = useMaintenance();
  const location = useLocation();
  // the one PUBLIC page: where a guest checkout lands after payment. It
  // renders without a session and outside the shell, and must never be
  // bounced to sign-in — the buyer may have no account yet, or be signed in
  // as somebody else (GuestPurchasePage says what to do in each case)
  const isPublic = matchPath("/purchase/guest", location.pathname) !== null;
  const dispatch = useDispatch();
  const signOut = useSignOut();
  // consent state of each legal document from /me; while any of them
  // requires acceptance the app shows only the consent dialog (see
  // PolicyConsentDialog)
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [bootstrap, setBootstrap] = useState<Bootstrap>({ status: "idle", attempt: 0 });
  // the bootstrap effect reads the token through a ref so a rotation while
  // /me is in flight neither cancels nor re-runs it
  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;
  const redirected = useRef(false);

  // App-wide gate: no session → the login app, which brings the user back
  // to this exact URL once signed in (the session is shared via cookies).
  // A deliberate sign-out (useSignOut) navigates on its own.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isExplicitSignOut() && !redirected.current && !isPublic) {
      redirected.current = true;
      window.location.replace(loginPageUrl());
    }
  }, [isLoading, isAuthenticated, isPublic])

  useEffect(() => {
    const token = tokenRef.current;
    if (!isAuthenticated || token === undefined || bootstrap.status !== "idle")
      return;
    setBootstrap(b => ({ ...b, status: "loading" }));
    Api.getMe(token)
      .then(me => {
        dispatch(logIn({ user: me.user, claimedOrders: me.claimedOrders, autoLinkedIlok: me.autoLinkedIlok }));
        setPolicies(me.policies);
        setBootstrap({ status: "ready", attempt: 0 });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : String(err);
        const unauthorized = err instanceof ApiError && err.status === 401;
        console.error("Could not get account data: " + message);
        setBootstrap(b => ({ status: "error", attempt: b.attempt + 1, message, unauthorized }));
      })
  }, [isAuthenticated, bootstrap.status, dispatch]);

  // retry a failed bootstrap: backoff timer, plus immediately when the tab
  // wakes up (visible / focus / back online)
  useEffect(() => {
    if (bootstrap.status !== "error")
      return;
    const retryNow = () => setBootstrap(b => b.status === "error" ? { ...b, status: "idle" } : b);
    const delay = BOOTSTRAP_RETRY_DELAYS_MS[Math.min(bootstrap.attempt - 1, BOOTSTRAP_RETRY_DELAYS_MS.length - 1)];
    const timer = setTimeout(retryNow, delay);
    const onWake = () => { if (document.visibilityState === "visible") retryNow() };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
    }
  }, [bootstrap.status, bootstrap.attempt]);

  // closed for maintenance (flipped by the first API call answered 503/
  // Maintenance — see api/maintenance.ts): the whole app becomes the
  // maintenance page, which reloads once the system is open again. Ahead
  // of every other gate, the public page included: a guest landing has
  // nothing to show while the store is closed either
  if (maintenance.closed)
    return <MaintenancePage maintenance={maintenance} accessToken={accessToken} />

  // the public page runs ahead of every gate; a signed-in visitor's
  // bootstrap /me still runs (the effect above only needs the session) and
  // the page reads its outcome — that call is what lands a parked purchase
  // on the account
  if (isPublic)
    return <GuestPurchasePage session={{ isLoading, isAuthenticated }} bootstrap={bootstrap} />

  if (isLoading)
    return (
      <FullScreen>
        {degraded && (
          <>
            <p>Reconnecting…</p>
            <button onClick={retry}>Retry now</button>
          </>
        )}
      </FullScreen>
    )

  if (!isAuthenticated)
    return (
      <FullScreen>
        <p style={{ marginBottom: 16 }}>Redirecting to sign in…</p>
      </FullScreen>
    )

  if (bootstrap.status === "error")
    return (
      <FullScreen>
        <p>Couldn't load your account. Retrying…</p>
        <p style={{ opacity: 0.7, fontSize: 13 }}>{bootstrap.message}</p>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => setBootstrap(b => ({ ...b, status: "idle" }))}>Retry now</button>
          {bootstrap.unauthorized && (
            <button onClick={() => { void signOut() }}>Sign in again</button>
          )}
        </div>
      </FullScreen>
    )


  // nothing renders before /me has answered: the pages' own fetches
  // (library, points, orders) must not race the account bootstrap — /me is
  // also where guest purchases parked for this email land on the account
  // (claimedOrders), and a library fetched a moment earlier would miss them
  // until the next reload
  if (bootstrap.status !== "ready")
    return <FullScreen>{null}</FullScreen>

  if (policies.some(p => p.acceptanceRequired) && accessToken)
    return (
      <PolicyConsentDialog
        policies={policies}
        accessToken={accessToken}
        onAccepted={(updated) => setPolicies(updated)}
      />
    )

  return (
    <>
      <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/licenses" element={<Library />} />
        <Route path="/installers" element={<Installers />} />
        <Route path="/ilok" element={<IlokPage />} />
        <Route path="/licenses/:id" element={<LicenseDetail />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/redemptions" element={<Redemptions />} />
        <Route path="/redemptions/:id" element={<RedemptionDetail />} />
        {/* hidden (not in the sidebar): one page per reseller code list
            (resellerPages.ts) — the reseller's own instructions carry the link */}
        {RESELLER_PAGES.map(page => (
          <Route key={page.path} path={page.path} element={<ResellerRedeemPage page={page} />} />
        ))}
        {/* FCS-hidden features (see featureFlags.ts): the routes stay
            registered so a stale link lands on the dashboard, not a blank page */}
        <Route path="/points" element={REWARD_POINTS_ENABLED ? <PointsPage /> : <Navigate to="/" replace />} />
        <Route path="/plan" element={RENT_TO_OWN_ENABLED ? <SubscriptionPage /> : <Navigate to="/" replace />} />
        <Route path="/buy" element={<BuyPage />} />
        <Route path="/purchase/success" element={<PurchaseSuccessPage />} />
        {/* temporarily withdrawn for customers, kept for system-wide admins (offersAccess.ts) */}
        <Route path="/offers" element={<OffersRoute />} />
        <Route path="/account" element={<AccountPage />} />
        {/* Admin/dev-only pages: role + MFA gated (see RequirePrivileged) */}
        <Route element={<RequirePrivileged />}>
          <Route path="/credits" element={<CreditsPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductPage />} />
          <Route path="/skus" element={<Skus />} />
          <Route path="/skus/:id" element={<SkuPage />} />
          <Route path="/skus/:id/users" element={<SkuEntitlements />} />
          <Route path="/organizations" element={<Organizations />} />
          <Route path="/organizations/:id" element={<OrganizationPage />} />
          <Route path="/users" element={<Users />} />
          <Route path="/users/:id" element={<UserPage />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetail />} />
        </Route>
      </Route>
      </Routes>
    </>
  );
}
